/**
 * "My gradients" — gradients the user saved from the gradient creator. Stored by the backend in one document (like font
 * schemes) so they are offered for every fill in every publication; kept here as a small in-memory store with listeners.
 */
import axios from 'axios';
import { normalizeGradient } from './gradients';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let presets = []; // [{ id, name, gradient }]
let loaded = null;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => { try { fn(presets); } catch { /* ignore */ } });

export const onGradientPresetsChanged = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

export const loadGradientPresets = (force = false) => {
  if (loaded && !force) return loaded;
  loaded = (async () => {
    try {
      const { data } = await axios.get(`${API}/fills/gradients`);
      presets = Array.isArray(data?.presets) ? data.presets.map((p) => ({ ...p, gradient: normalizeGradient(p.gradient) })) : [];
      notify();
    } catch (e) {
      console.warn('[gradients] presets unavailable', e?.message || e); // eslint-disable-line no-console
    }
    return presets;
  })();
  return loaded;
};

export const getGradientPresets = () => presets;

const persist = async (next) => {
  const prev = presets;
  presets = next;
  notify();
  try {
    const { data } = await axios.put(`${API}/fills/gradients`, { presets: next.map((p) => ({ id: p.id, name: p.name, gradient: p.gradient })) });
    if (Array.isArray(data?.presets)) { presets = data.presets.map((p) => ({ ...p, gradient: normalizeGradient(p.gradient) })); notify(); }
    return presets;
  } catch (e) {
    presets = prev;
    notify();
    throw e;
  }
};

/** Save (or overwrite a same-named) preset. Resolves to the saved preset. */
export const saveGradientPreset = async (name, gradient) => {
  const n = String(name || '').trim().slice(0, 60);
  if (!n) throw new Error('Give the gradient a name.');
  const g = normalizeGradient(gradient);
  const existing = presets.find((p) => p.name.toLowerCase() === n.toLowerCase());
  const preset = existing ? { ...existing, gradient: g } : { id: `user:${crypto.randomUUID ? crypto.randomUUID() : Date.now()}`, name: n, gradient: g };
  const next = existing ? presets.map((p) => (p.id === existing.id ? preset : p)) : [...presets, preset];
  await persist(next);
  return preset;
};

export const deleteGradientPreset = async (id) => persist(presets.filter((p) => p.id !== id));
export const renameGradientPreset = async (id, name) => persist(presets.map((p) => (p.id === id ? { ...p, name: String(name || '').trim().slice(0, 60) || p.name } : p)));
